import React from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import { withGoogleReCaptcha } from 'react-google-recaptcha-v3';

import WrappedField from '../WrappedField';
import Button from '../Button';
import PreTitle from '../PreTitle';
import Form from '../Form';
import { encode } from '../../helpers/encode';
import { ajaxSuccess } from '../../helpers/tagManager';
import StatementContact from '../StatementContact';

const formSchema = Yup.object().shape({
  name: Yup.string().min(2, 'Name must be at least 2 characters').required('Name is required'),
  email: Yup.string()
    .email('Email is invalid')
    .required('Email is required'),
  company: Yup.string(),
  found: Yup.string(),
  message: Yup.string().min(2, 'Message must be at least 2 characters').required('Message is required'),
  newsletter: Yup.boolean().oneOf([true], 'You need to accept it to submit this form'),
});

const initialValues = {
  name: '',
  email: '',
  company: '',
  found: '',
  message: '',
  newsletter: false,
  contactSuccessCb: null
};

class Contact extends React.Component {
  constructor(props) {
    super(props);

    this.idHTML = 'contact';
    this.netlifyFormName = 'contact';
    this.netlifyHoneypotName = 'address';
    this.fieldNames = ['name', 'email', 'company', 'message', 'newsletter', 'found'];

    this.state = {
      hasSubmitSucceeded: false,
      hasSubmitFailed: false,
      recaptchaValue: '',
      token: ''
    };
  }

  get isNetlifyFormActive() {
    return process.env.GATSBY_IS_NETLIFY_FORM_ACTIVE === 'TRUE';
  }

  prepareData = values => {
    const { fieldNames, isNetlifyFormActive } = this;

    const netlifyData = isNetlifyFormActive
      ? { 'form-name': this.netlifyFormName }
      : {};

    if (isNetlifyFormActive) {
      fieldNames.push(this.netlifyHoneypotName);
    }

    return fieldNames.reduce((acc, fieldName) => {
      const val =
        typeof values[fieldName] === 'object'
          ? values[fieldName].value
          : values[fieldName];
      acc[fieldName] = val || '';
      return acc;
    }, netlifyData);
  };

  sendForm = async data => {
    const { executeRecaptcha } = this.props.googleReCaptchaProps;
    const { contactSuccessCb } = this.props;

    const url = '/.netlify/functions/recaptcha';

    if (!executeRecaptcha) {
      this.setState({ hasSubmitFailed: true });
      return
    }

    const resultToken = await executeRecaptcha('homepage');
    this.setState({ token: resultToken });

    const resultCaptcha = await axios({
      method: 'get',
      url: url,
      params: {
        token: resultToken,
      }
    }).then((res) => res.data)

    if (!resultCaptcha?.success || resultCaptcha?.score <= 0.7) {
      this.setState({ hasSubmitFailed: true });
      return
    }

    const res = await fetch('/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: encode(data),
    });
    const newState = res.ok
      ? { hasSubmitSucceeded: true }
      : { hasSubmitFailed: true };

    if (res.ok) {
      ajaxSuccess(this.idHTML);

      if (contactSuccessCb) {
        contactSuccessCb();
      }
    }

    this.setState(newState);
  };

  onSubmit = values => {
    const data = this.prepareData(values);
    this.sendForm(data);
  };

  render() {
    const { hasSubmitSucceeded, hasSubmitFailed } = this.state;
    const { title, text } = this.props;

    const netlifyProps = this.isNetlifyFormActive
      ? {
        name: this.netlifyFormName,
        'data-netlify': true,
        'data-netlify-honeypot': this.netlifyHoneypotName,
      }
      : {};

    let submit = (
      <Button type="submit" className="Contact__submit Form__submit" variants={['wide']}>
        Send
      </Button>
    );
    if (hasSubmitSucceeded) {
      submit = (
        <p className="Form__submit">
          Thanks for reaching out! We will get back to you shortly.
        </p>
      );
    } else if (hasSubmitFailed) {
      submit = (
        <p className="Form__submit">
          Your form was <strong>not</strong> saved. Please reach us at{' '}
          <strong>hello@getindata.com</strong>. Thanks!
        </p>
      );
    }

    return (
      <section className="Contact" id="contact">
        <div className="Contact__container">
          <div id="sectionContact" className="Contact__content">
            <PreTitle
              className="Contact__subTitle"
              title={'Contact us'}
            />

            <h2 className="Contact__title">
              {title.length > 0 ? title : <div>Interested in our solutions? <br />Contact us!</div>}
            </h2>

            <p className="Contact__text pb-5">
              {text.length > 0 ? text : "Together, we will select the best Big Data solutions for your organization and build a project that will have a real impact on your organization."}
            </p>

            <hr className="text-white bg-white h-[3px] mt-5 mb-5 width-[80%]" />

            <p className="font-bold pt-5 pb-5">
              What did you find most impressive about GetInData?
            </p>

            <div className="Contact__quote text-xl italic mt-5">
              They did a very good job in finding people that fitted in Acast both technically as well as culturally.
            </div>
          </div>

          <div className="Contact__form-wrapper">
            <div className="mb-5 pb-3">
              Type the form or send a e-mail: <a href={`mailto:hello@getindata.com`}>hello@getindata.com</a>
            </div>

            <Formik
              validationSchema={formSchema}
              onSubmit={this.onSubmit}
              initialValues={initialValues}
            >
              {({
                errors,
                touched,
                setFieldValue,
                setFieldTouched,
                values,
              }) => (
                <>
                  {/* <h3 className="Form__title Contact__form-title">
                    {title ? title : 'Any questions?'}
                  </h3> */}

                  <Form {...netlifyProps}>
                    {this.isNetlifyFormActive && (
                      <input type="hidden" name={this.netlifyHoneypotName} />
                    )}
                    {this.isNetlifyFormActive && (
                      <input
                        type="hidden"
                        name="form-name"
                        value={this.netlifyFormName}
                      />
                    )}

                    <input
                      type="hidden"
                      name="idHTML"
                      value={this.idHTML}
                    />

                    <div className="Form__level">
                      <WrappedField
                        name="name"
                        label="Your name"
                        type="text"
                        error={errors.name}
                        isTouched={touched.name}
                        placeholder="Type your name"
                      />
                    </div>

                    <div className="Form__level">
                      <WrappedField
                        name="email"
                        label="Email"
                        error={errors.email}
                        isTouched={touched.email}
                        placeholder="Type email"
                      />
                    </div>

                    <div className="Form__level">
                      <WrappedField
                        name="company"
                        label="Company"
                        error={errors.company}
                        isTouched={touched.company}
                        placeholder="Type your company"
                      />
                    </div>

                    <div className="Form__level">
                      <WrappedField
                        name="found"
                        label="How did you find us?"
                        type="text"
                        error={errors.found}
                        isTouched={touched.found}
                        placeholder="Type your answer"
                      />
                    </div>

                    <div className="Form__level">
                      <WrappedField
                        name="message"
                        label="Message"
                        error={errors.message}
                        isTouched={touched.message}
                        placeholder="Type message"
                        component="textarea"
                      />
                    </div>

                    <div className="Form__level">
                      <WrappedField
                        name="newsletter"
                        label="I agree to receive via email marketing information (e.g. about news, products and services, events or training courses)"
                        error={errors.newsletter}
                        isTouched={touched.newsletter}
                        type="checkbox"
                        isCheckbox={true}
                      />
                    </div>

                    <div className="Contact__statement">
                      <StatementContact />
                    </div>

                    {submit}
                  </Form>
                </>
              )}
            </Formik>
          </div>
        </div>
      </section>
    );
  }
}

export default withGoogleReCaptcha(Contact);